import type { AddressMode, ArrivalDto, AssignmentKind, DoorStyle, TurfContactDto, WorksiteInfoDto } from '../types/canvasser'
import { haversineMeters } from './walkGeo'

/**
 * [Spec §5.4] The ONE status vocabulary for the walk. List rows, map pins, the dock, filters and
 * progress all read this module — replacing the four drifting "needs revisit" definitions.
 * Phase 3 switches the "revisit" source from the campaign ContactStatus to the last recorded
 * outcome (Busy/LanguageBarrier/Inaccessible arrive then); callers never change.
 */
export type WalkState = 'untouched' | 'revisit' | 'done'
export type StopKind = 'home' | 'worksite'
export type StatusFilter = 'remaining' | 'revisit' | 'all'
/** Side of the street: a pair splits a shared turf, one taking the odd numbers, the other the even. */
export type StreetSide = 'all' | 'odd' | 'even'

export interface WalkStop {
  key: string
  kind: StopKind
  /** Street line incl. unit, as shown on the row. */
  address: string
  /** Street name without the house number, for grouping. */
  street: string
  city: string
  zip: string
  lat: number | null
  lng: number | null
  /** Lowest resident walk order: the stop's base position. */
  walkOrder: number
  /** Residents (or employees) in walk order. */
  residents: TurfContactDto[]
  workplaceLabel: string | null
  state: WalkState
  /** [§15.1 S4] Worksite stops only: "Done here" was tapped / a visit recorded here today. */
  visitedToday: boolean
  /** Household DNC alert — home stops only. */
  hasDnc: boolean
  /** [P4-S2] Worksite stops: "Before you go in". */
  arrival: ArrivalDto | null
  /** [O3] Worksite stops: manager + description for the "Worksite info" sheet. */
  worksiteInfo: WorksiteInfoDto | null
  /** [D4] Home stops created by "Visit at home today": the worksite they moved from. */
  movedFrom: string | null
}

/** What the canvasser left the walk screen to visit (§15.1 S5 return rule). */
export interface LeftStop {
  key: string
  state: WalkState
  /** `stopSignature` at the moment the canvasser left. Optional for backward compat. */
  sig?: string
  /** [Phase 4] Set by the door flow after it recorded something here: the dock always advances past it,
   *  even when the signature didn't change (an identical re-record at a single-resident door). */
  recorded?: boolean
}

const REVISIT_STATUSES = new Set(['NoAnswer', 'Callback'])

export function contactWalkState(c: TurfContactDto): WalkState {
  if (c.isDoNotContact) return 'done'
  if (!c.isVisited) return 'untouched'
  if (c.hasComeBackLater || REVISIT_STATUSES.has(c.status)) return 'revisit'
  return 'done'
}

/**
 * [CF-1 P3] An action stop (sign drop-off etc.) is done once visited or its follow-up item closed
 * (`followUpOpen === false`, e.g. the organizer cancelled it). It is never 'revisit': a NoAnswer
 * status must not turn a stop into a retry.
 */
export function actionStopState(c: TurfContactDto): WalkState {
  return c.isVisited || c.followUpOpen === false ? 'done' : 'untouched'
}

/** The per-contact state rule for a turf's assignment kind. Action routes use `actionStopState`. */
export function stateRuleFor(kind: AssignmentKind | undefined): (c: TurfContactDto) => WalkState {
  return kind === 'ActionRoute' ? actionStopState : contactWalkState
}

/**
 * [CF-1 P5] "Already voted — skip": a person row (or the person the dock names) reads that person;
 * a household row only when everyone at the door already voted (one voter left = still knock).
 */
export function stopAlreadyVoted(stop: WalkStop, contactId?: string | null): boolean {
  if (contactId) return stop.residents.find(r => r.contactId === contactId)?.alreadyVoted === true
  return stop.residents.length > 0 && stop.residents.every(r => r.alreadyVoted === true)
}

export function stopKeyOf(c: TurfContactDto): string {
  return c.householdKey || `${c.streetAddress}|${c.zip}`
}

/** Work/Hybrid: the server's per-member `stopLocation` decides (a Work-turf member with no active
 *  employment falls back to Home). Only an old cache without it falls back to the address mode. */
function stopKindOf(c: TurfContactDto, addressMode: AddressMode): StopKind {
  if (addressMode === 'Home') return 'home'
  if (c.stopLocation) return c.stopLocation === 'Work' ? 'worksite' : 'home'
  return addressMode === 'Work' ? 'worksite' : 'home'
}

function streetOf(address: string): string {
  return address.replace(/^\d+[A-Za-z]?\s+/, '').trim() || address
}

function rollUp(
  residents: TurfContactDto[], kind: StopKind, visitedToday: boolean, stateOf: (c: TurfContactDto) => WalkState,
): WalkState {
  if (kind === 'worksite' && visitedToday) return 'done'
  const states = residents.map(stateOf)
  if (states.every(s => s === 'done')) return 'done'
  if (states.includes('revisit')) return 'revisit'
  return 'untouched'
}

export function buildWalkStops(
  contacts: TurfContactDto[],
  addressMode: AddressMode,
  doneHereKeys: ReadonlySet<string>,
  stateOf: (c: TurfContactDto) => WalkState = contactWalkState,
): WalkStop[] {
  const groups = new Map<string, TurfContactDto[]>()
  for (const c of contacts) {
    const key = stopKeyOf(c)
    const list = groups.get(key)
    if (list) list.push(c)
    else groups.set(key, [c])
  }
  const stops: WalkStop[] = []
  for (const [key, members] of groups) {
    const residents = [...members].sort((a, b) => a.walkOrder - b.walkOrder)
    const first = residents[0]
    const kind = stopKindOf(first, addressMode)
    const located = residents.find(r => r.latitude != null && r.longitude != null)
    const visitedToday = kind === 'worksite' && doneHereKeys.has(key)
    stops.push({
      key,
      kind,
      address: first.streetAddress + (first.unitNumber ? ` ${first.unitNumber}` : ''),
      street: streetOf(first.streetAddress),
      city: first.city,
      zip: first.zip,
      lat: located?.latitude ?? null,
      lng: located?.longitude ?? null,
      walkOrder: first.walkOrder,
      residents,
      workplaceLabel: kind === 'worksite' ? first.workplaceLabel : null,
      state: rollUp(residents, kind, visitedToday, stateOf),
      visitedToday,
      hasDnc: kind === 'home' && residents.some(r => r.isDoNotContact),
      arrival: kind === 'worksite' ? residents.find(r => r.arrival)?.arrival ?? null : null,
      worksiteInfo: kind === 'worksite' ? residents.find(r => r.worksiteInfo)?.worksiteInfo ?? null : null,
      movedFrom: kind === 'home' ? residents.find(r => r.movedFromWorksite)?.movedFromWorksite ?? null : null,
    })
  }
  return stops.sort((a, b) => a.walkOrder - b.walkOrder || a.key.localeCompare(b.key))
}

export function matchesFilter(stop: WalkStop, filter: StatusFilter): boolean {
  if (filter === 'all') return true
  if (filter === 'revisit') return stop.state === 'revisit'
  return stop.state !== 'done'
}

/** The leading house number of an address ("7B Oak Ave" -> 7), or null when there isn't one. */
export function houseNumberOf(address: string): number | null {
  const m = /^\s*(\d+)/.exec(address)
  return m ? Number(m[1]) : null
}

/** Odd/even side filter. A stop with no house number matches both sides, so it never silently vanishes. */
export function matchesSide(stop: WalkStop, side: StreetSide): boolean {
  if (side === 'all') return true
  const n = houseNumberOf(stop.address)
  if (n == null) return true
  return side === 'odd' ? n % 2 === 1 : n % 2 === 0
}

export function matchesSearch(stop: WalkStop, q: string): boolean {
  const needle = q.trim().toLowerCase()
  if (!needle) return true
  if (`${stop.address} ${stop.city}`.toLowerCase().includes(needle)) return true
  if (stop.workplaceLabel?.toLowerCase().includes(needle)) return true
  return stop.residents.some(r =>
    `${r.firstName} ${r.lastName}`.toLowerCase().includes(needle) || (r.phone?.includes(needle) ?? false))
}

/** Route order first (a frozen optimized order, §16), then everything else by walk order. */
export function orderStops(stops: WalkStop[], order: readonly string[] | null): WalkStop[] {
  if (!order || order.length === 0) return stops
  const rank = new Map(order.map((k, i) => [k, i]))
  const inOrder = stops.filter(s => rank.has(s.key)).sort((a, b) => rank.get(a.key)! - rank.get(b.key)!)
  const rest = stops.filter(s => !rank.has(s.key))
  return [...inOrder, ...rest]
}

/** 1-based pin/row numbers. List and map read the same map, so their numbers always agree. */
export function sequenceOf(ordered: WalkStop[]): Map<string, number> {
  return new Map(ordered.map((s, i) => [s.key, i + 1]))
}

export function walkProgress(stops: WalkStop[]): { done: number; total: number; revisit: number } {
  return {
    done: stops.filter(s => s.state === 'done').length,
    total: stops.length,
    revisit: stops.filter(s => s.state === 'revisit').length,
  }
}

/**
 * [§15.1 S5] After a stop is recorded, the dock offers the next unfinished stop AFTER it in walk
 * order, wrapping round to skipped earlier stops; the just-recorded stop comes last.
 */
export function nextStopAfter(ordered: WalkStop[], afterKey: string | null): WalkStop | null {
  const open = (s: WalkStop) => s.state !== 'done'
  const i = afterKey == null ? -1 : ordered.findIndex(s => s.key === afterKey)
  if (i < 0) return ordered.find(open) ?? null
  const rotated = [...ordered.slice(i + 1), ...ordered.slice(0, i), ordered[i]]
  return rotated.find(open) ?? null
}

/**
 * A deterministic fingerprint of everything a recorded visit can change on a stop: each resident's
 * `contactId`, `isVisited`, `status` and `hasComeBackLater` (in resident order), plus the stop's
 * `visitedToday`. The walk screen captures it when the canvasser leaves for a stop and compares it
 * on return — this is how it tells "backed out without recording" (same signature) from
 * "recorded" (signature changed), even when the rolled-up state is unchanged (revisit → revisit).
 */
export function stopSignature(stop: WalkStop): string {
  const people = stop.residents
    .map(r => `${r.contactId}:${r.isVisited ? 1 : 0}:${r.status}:${r.hasComeBackLater ? 1 : 0}`)
    .join(',')
  return `${people}|${stop.visitedToday ? 1 : 0}`
}

/**
 * Back on the walk screen: if the stop the canvasser left for still exists, is still open, and is
 * unchanged — same `stopSignature` when the caller captured one, else (legacy) same rolled-up
 * state — they backed out without recording, so offer it again. Otherwise something was recorded
 * there: apply the S5 rule from it (advance past it, wrap round, it comes last).
 */
export function dockStopOnReturn(ordered: WalkStop[], left: LeftStop | null): WalkStop | null {
  if (left?.recorded) return nextStopAfter(ordered, left.key)
  if (left) {
    const same = ordered.find(s => s.key === left.key)
    if (same && same.state !== 'done') {
      const unchanged = left.sig != null ? stopSignature(same) === left.sig : same.state === left.state
      if (unchanged) return same
    }
  }
  return nextStopAfter(ordered, left?.key ?? null)
}

/** The first resident not yet Done (the dock's default person). */
export function firstOpenResident(stop: WalkStop): TurfContactDto {
  return stop.residents.find(r => contactWalkState(r) !== 'done') ?? stop.residents[0]
}

/**
 * [Spec §15.1 D4] A stop moved home today is cheapest-inserted into the walk order on the phone: it goes
 * between the two consecutive located stops where it adds the least straight-line distance (or before
 * the first / after the last). Deterministic over the full ordered list (done stops included), so it never
 * jumps around as the walk progresses. Unlocated moved stops stay where they are, and so do moved stops
 * whose key is in `placed` (the route order already places them — R26).
 */
export function placeMovedHomeStops(ordered: WalkStop[], placed?: ReadonlySet<string>): WalkStop[] {
  const moved = ordered.filter(s => s.movedFrom && s.lat != null && s.lng != null && !placed?.has(s.key))
  if (moved.length === 0) return ordered
  const d = (a: WalkStop, b: WalkStop) => haversineMeters(a.lat!, a.lng!, b.lat!, b.lng!)
  let out = ordered.filter(s => !moved.includes(s))
  for (const m of moved) {
    const located = out.filter(s => s.lat != null && s.lng != null)
    if (located.length === 0) { out = [...out, m]; continue }
    // Candidate 0: before the first located stop. Then: between each pair, and after the last.
    let bestCost = d(m, located[0])
    let insertBefore: WalkStop | null = located[0]
    for (let i = 0; i < located.length; i++) {
      const a = located[i]
      const b = located[i + 1]
      const cost = b ? d(a, m) + d(m, b) - d(a, b) : d(a, m)
      if (cost < bestCost) { bestCost = cost; insertBefore = b ?? null }
    }
    if (insertBefore === null) out = [...out, m]
    else {
      const at = out.indexOf(insertBefore)
      out = [...out.slice(0, at), m, ...out.slice(at)]
    }
  }
  return out
}

/**
 * [A3 T8] Fallback stop order when routing is unavailable: nearest-neighbour from `from` (or from the
 * first located stop when `from` is null), then 2-opt on the open path (start fixed, <= 50 passes;
 * skipped above APPROX_2OPT_MAX_STOPS located stops).
 * Returns keys of LOCATED stops only; unlocated ones are left to `orderStops`' "rest". Pure and
 * deterministic (ties break by input order).
 */
/** Above this many located stops `approximateOrder` skips 2-opt (NN only), so a big turf never janks. */
export const APPROX_2OPT_MAX_STOPS = 99

export function approximateOrder(
  stops: WalkStop[],
  from: { lat: number; lng: number } | null,
): string[] {
  const pts = stops.filter(s => s.lat != null && s.lng != null)
  const n = pts.length
  if (n === 0) return []
  if (n === 1) return [pts[0].key]
  const dist = (i: number, j: number) => haversineMeters(pts[i].lat!, pts[i].lng!, pts[j].lat!, pts[j].lng!)

  let start = 0
  if (from) {
    let best = Infinity
    for (let i = 0; i < n; i++) {
      const d = haversineMeters(from.lat, from.lng, pts[i].lat!, pts[i].lng!)
      if (d < best) { best = d; start = i }
    }
  }
  const path = [start]
  const used = new Array<boolean>(n).fill(false)
  used[start] = true
  while (path.length < n) {
    const cur = path[path.length - 1]
    let next = -1
    let best = Infinity
    for (let i = 0; i < n; i++) {
      if (used[i]) continue
      const d = dist(cur, i)
      if (d < best) { best = d; next = i }
    }
    used[next] = true
    path.push(next)
  }

  // Big turf: 2-opt is O(n^2) per pass on the main thread — nearest-neighbour only (final review).
  if (n > APPROX_2OPT_MAX_STOPS) return path.map(i => pts[i].key)
  // 2-opt on the open path; index 0 stays fixed. Reversing path[i..j] swaps edges (i-1,i) and (j,j+1).
  const EPS = 1e-9
  for (let pass = 0; pass < 50; pass++) {
    let improved = false
    for (let i = 1; i < n - 1; i++) {
      for (let j = i + 1; j < n; j++) {
        const a = path[i - 1], b = path[i], c = path[j], d = j + 1 < n ? path[j + 1] : -1
        const before = dist(a, b) + (d >= 0 ? dist(c, d) : 0)
        const after = dist(a, c) + (d >= 0 ? dist(b, d) : 0)
        if (after < before - EPS) {
          for (let l = i, r = j; l < r; l++, r--) [path[l], path[r]] = [path[r], path[l]]
          improved = true
        }
      }
    }
    if (!improved) break
  }
  return path.map(i => pts[i].key)
}

/** The door page for a stop (Phase 4). `contactId` is passed for a person-first (contact-walk) door. */
export function doorPath(turfId: string, stopKey: string, contactId?: string | null): string {
  const base = `/canvasser/door/${encodeURIComponent(stopKey)}?turfId=${encodeURIComponent(turfId)}`
  return contactId ? `${base}&contactId=${encodeURIComponent(contactId)}` : base
}

/** [P4-1] A contact walk (person-first door): the server says so via doorStyle. */
export function isPersonFirstTurf(turf: { doorStyle?: DoorStyle } | undefined): boolean {
  return turf?.doorStyle === 'Person'
}
